import { useState, useRef, useEffect, useCallback } from 'react'
import { BrowserMultiFormatReader, IScannerControls } from '@zxing/browser'
import { BarcodeFormat, DecodeHintType, NotFoundException, ChecksumException, FormatException, Result } from '@zxing/library'

type ScanResult = {
  text: string
  format: string
  timestamp: number
}

// Every 1D/2D format zxing-js can decode on its own (excludes UPC_EAN_EXTENSION,
// which is only ever read as an add-on to an EAN/UPC code, never standalone).
const READABLE_FORMATS: BarcodeFormat[] = [
  BarcodeFormat.QR_CODE,
  BarcodeFormat.MICRO_QR_CODE,
  BarcodeFormat.AZTEC,
  BarcodeFormat.DATA_MATRIX,
  BarcodeFormat.MAXICODE,
  BarcodeFormat.PDF_417,
  BarcodeFormat.CODABAR,
  BarcodeFormat.CODE_39,
  BarcodeFormat.CODE_93,
  BarcodeFormat.CODE_128,
  BarcodeFormat.EAN_8,
  BarcodeFormat.EAN_13,
  BarcodeFormat.ITF,
  BarcodeFormat.RSS_14,
  BarcodeFormat.RSS_EXPANDED,
  BarcodeFormat.UPC_A,
  BarcodeFormat.UPC_E,
]

function formatName(format: BarcodeFormat): string {
  return BarcodeFormat[format].replace(/_/g, ' ')
}

// 'focusMode' is a real, widely-implemented (Chrome/Android) constraint but
// isn't part of TypeScript's DOM lib typings yet.
type ExtendedTrackConstraintSet = MediaTrackConstraintSet & {
  focusMode?: 'continuous' | 'single-shot' | 'manual' | 'none'
}

function buildVideoConstraints(deviceId?: string): MediaStreamConstraints {
  const source: MediaTrackConstraintSet = deviceId
    ? { deviceId: { exact: deviceId } }
    : { facingMode: 'environment' }
  return {
    video: {
      ...source,
      // A bigger frame gives the decoder more pixels to resolve small/dense
      // codes with - most phone cameras default to a much lower profile
      // unless a resolution is explicitly requested. "ideal" is a soft
      // constraint, so this never fails on cameras that can't reach it.
      width: { ideal: 1920 },
      height: { ideal: 1080 },
      advanced: [{ focusMode: 'continuous' } as ExtendedTrackConstraintSet],
    },
  }
}

function getFriendlyErrorMessage(error: unknown): string {
  const name = (error as { name?: string } | undefined)?.name
  switch (name) {
    case 'NotAllowedError':
      return 'Camera access was denied. Allow camera access for this site in your browser settings and try again.'
    case 'NotFoundError':
    case 'OverconstrainedError':
      return 'No usable camera could be found on this device.'
    case 'NotReadableError':
      return 'The camera is already in use by another application.'
    case 'SecurityError':
      return 'Camera access requires a secure (HTTPS) connection.'
    default:
      return (error as { message?: string } | undefined)?.message || 'Unable to access the camera.'
  }
}

function BarCode() {
  const videoRef = useRef<HTMLVideoElement>(null)
  const readerRef = useRef<BrowserMultiFormatReader | null>(null)
  const controlsRef = useRef<IScannerControls | null>(null)

  const [isScanning, setIsScanning] = useState(false)
  const [starting, setStarting] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [devices, setDevices] = useState<MediaDeviceInfo[]>([])
  const [selectedDeviceId, setSelectedDeviceId] = useState<string>('')
  const [results, setResults] = useState<ScanResult[]>([])
  const [torchOn, setTorchOn] = useState(false)
  const [torchSupported, setTorchSupported] = useState(false)
  const [justFound, setJustFound] = useState(false)
  const foundTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  const cameraSupported = typeof navigator !== 'undefined' && !!navigator.mediaDevices?.getUserMedia
  const isSecureContext = typeof window === 'undefined' || window.isSecureContext
  const unsupportedReason = !cameraSupported
    ? 'This browser does not support camera access.'
    : !isSecureContext
      ? 'Camera access requires a secure connection (HTTPS).'
      : null

  const getReader = () => {
    if (!readerRef.current) {
      const hints = new Map<DecodeHintType, unknown>()
      hints.set(DecodeHintType.POSSIBLE_FORMATS, READABLE_FORMATS)
      // Spend more effort per frame trying to find a code - meaningfully
      // improves the hit rate on angled, small, or lower-contrast codes.
      hints.set(DecodeHintType.TRY_HARDER, true)
      readerRef.current = new BrowserMultiFormatReader(hints, {
        // Attempt a new decode as soon as the previous one finishes rather
        // than waiting the library's 500ms default - more attempts per
        // second means more chances to catch a frame that's in focus.
        delayBetweenScanAttempts: 75,
        delayBetweenScanSuccess: 500,
      })
    }
    return readerRef.current
  }

  const handleDecode = useCallback((result?: Result, err?: unknown) => {
    if (result) {
      const text = result.getText()
      const format = formatName(result.getBarcodeFormat())
      setResults(prev => {
        if (prev.length && prev[0].text === text && prev[0].format === format) {
          return prev
        }
        return [{ text, format, timestamp: Date.now() }, ...prev].slice(0, 20)
      })

      // Keep the border green for 3s from the *last* detection, so it stays
      // solid green the whole time a code is held in view.
      setJustFound(true)
      if (foundTimeoutRef.current) clearTimeout(foundTimeoutRef.current)
      foundTimeoutRef.current = setTimeout(() => {
        setJustFound(false)
        foundTimeoutRef.current = null
      }, 3000)
      return
    }
    // A "not found" / checksum / format exception fires on almost every frame
    // where no code is in view yet - that's normal and not worth surfacing.
    if (err && !(err instanceof NotFoundException) && !(err instanceof ChecksumException) && !(err instanceof FormatException)) {
      console.error(err)
    }
  }, [])

  const startScanning = useCallback(async (deviceId?: string) => {
    if (!cameraSupported || !videoRef.current) return
    setError(null)
    setStarting(true)

    controlsRef.current?.stop()
    controlsRef.current = null

    try {
      const reader = getReader()
      const controls = await reader.decodeFromConstraints(
        buildVideoConstraints(deviceId || undefined),
        videoRef.current,
        (result, err) => handleDecode(result, err)
      )
      controlsRef.current = controls
      setIsScanning(true)
      setTorchOn(false)
      setTorchSupported(!!controls.switchTorch)

      const activeStream = videoRef.current.srcObject as MediaStream | null
      const activeDeviceId = activeStream?.getVideoTracks()[0]?.getSettings().deviceId
      if (activeDeviceId) setSelectedDeviceId(activeDeviceId)

      // Device labels are only populated once permission has been granted,
      // so we (re-)enumerate here rather than before starting the camera.
      try {
        const videoDevices = await BrowserMultiFormatReader.listVideoInputDevices()
        setDevices(videoDevices)
      } catch {
        // Listing extra cameras is a nice-to-have; scanning still works without it.
      }
    } catch (e) {
      setIsScanning(false)
      setError(getFriendlyErrorMessage(e))
    } finally {
      setStarting(false)
    }
  }, [cameraSupported, handleDecode])

  const stopScanning = useCallback(() => {
    controlsRef.current?.stop()
    controlsRef.current = null
    setIsScanning(false)
    setTorchSupported(false)
    setTorchOn(false)
    if (foundTimeoutRef.current) {
      clearTimeout(foundTimeoutRef.current)
      foundTimeoutRef.current = null
    }
    setJustFound(false)
  }, [])

  const handleCameraChange = (deviceId: string) => {
    setSelectedDeviceId(deviceId)
    if (isScanning) startScanning(deviceId)
  }

  const toggleTorch = useCallback(async () => {
    if (!controlsRef.current?.switchTorch) return
    const next = !torchOn
    try {
      await controlsRef.current.switchTorch(next)
      setTorchOn(next)
    } catch {
      // Torch control is experimental and not guaranteed on every device.
    }
  }, [torchOn])

  const copyToClipboard = async (text: string) => {
    try {
      await navigator.clipboard.writeText(text)
    } catch {
      // Clipboard API may be unavailable (e.g. insecure context) - safe to ignore.
    }
  }

  // Release the camera when the component unmounts (e.g. the user switches tabs).
  useEffect(() => {
    return () => {
      controlsRef.current?.stop()
      controlsRef.current = null
      if (foundTimeoutRef.current) clearTimeout(foundTimeoutRef.current)
    }
  }, [])

  const latest = results[0]

  return <div className={`w-full h-full flex flex-col justify-center items-center gap-2 p-6`}>
    <div className="inner w-full max-w-md">
      <h1 className="text-2xl font-bold mb-2">Scan a barcode</h1>
      <p className="text-sm text-gray-600 mb-4">
        Reads QR, Aztec, Data Matrix, PDF417, MaxiCode and common 1D barcodes (UPC, EAN, Code 128/39/93, ITF, Codabar, RSS) using your camera.
      </p>

      {(unsupportedReason || error) && (
        <div className="w-full rounded bg-red-100 text-red-800 text-sm p-3 mb-3">
          {unsupportedReason || error}
        </div>
      )}

      <div className="relative w-full rounded overflow-hidden bg-black" style={{ aspectRatio: '4 / 3' }}>
        <video
          ref={videoRef}
          muted
          playsInline
          className="w-full h-full object-cover"
        />
        {isScanning && (
          <div
            className={`pointer-events-none absolute inset-8 rounded ${
              justFound
                ? 'border-green-400 border-4 '
                : 'border-blue-400 animate-pulse border-2'
            }`}
          />
        )}
        {!isScanning && (
          <div className="absolute inset-0 flex items-center justify-center text-white text-sm bg-black/40 text-center px-4">
            Camera preview will appear here
          </div>
        )}
      </div>

      <div className="w-full flex flex-row gap-1 mt-3">
        {!isScanning ? (
          <button
            disabled={!cameraSupported || !isSecureContext || starting}
            className="w-full rounded font-bold py-2 px-4 text-sm bg-lime-700 text-white disabled:opacity-50"
            onClick={() => startScanning(selectedDeviceId)}
          >
            {starting ? 'Starting camera…' : 'Start scanning'}
          </button>
        ) : (
          <button
            className="w-full rounded font-bold py-2 px-4 text-sm bg-gray-700 text-white"
            onClick={stopScanning}
          >
            Stop scanning
          </button>
        )}

        {torchSupported && (
          <button
            className="rounded font-bold py-2 px-4 text-sm bg-lime-700 text-white whitespace-nowrap"
            onClick={toggleTorch}
          >
            {torchOn ? 'Flash off' : 'Flash on'}
          </button>
        )}
      </div>

      {devices.length > 1 && (
        <div className="w-full mt-2">
          <label className="text-sm">Camera:</label>
          <select
            className="w-full border border-gray-300 rounded py-2 px-3 text-sm"
            value={selectedDeviceId}
            onChange={(e) => handleCameraChange(e.target.value)}
          >
            {devices.map((d, i) => (
              <option key={d.deviceId} value={d.deviceId}>{d.label || `Camera ${i + 1}`}</option>
            ))}
          </select>
        </div>
      )}

      {latest && (
        <div className="w-full mt-4">
          <div className="flex flex-row justify-between items-center mb-1">
            <p className="text-sm font-bold">Last scanned</p>
            <button className="text-xs underline text-gray-500" onClick={() => setResults([])}>Clear</button>
          </div>

          <div className="w-full rounded bg-lime-50 border border-lime-200 p-3">
            <span className="inline-block text-xs font-bold uppercase bg-lime-700 text-white rounded px-2 py-0.5 mr-2 mb-1">
              {latest.format}
            </span>
            <span className="break-all text-sm align-middle">{latest.text}</span>
            <div className="mt-2">
              <button
                className="text-xs rounded py-1 px-3 bg-lime-700 text-white"
                onClick={() => copyToClipboard(latest.text)}
              >
                Copy
              </button>
            </div>
          </div>

          {results.length > 1 && (
            <ul className="w-full max-h-40 overflow-y-auto divide-y divide-gray-200 text-sm mt-2">
              {results.slice(1).map((r) => (
                <li key={`${r.format}-${r.text}-${r.timestamp}`} className="py-2 flex flex-row justify-between items-center gap-2">
                  <div className="min-w-0">
                    <span className="inline-block text-[10px] font-bold uppercase bg-gray-500 text-white rounded px-1.5 py-0.5 mr-2">{r.format}</span>
                    <span className="break-all">{r.text}</span>
                  </div>
                  <button
                    className="text-xs underline text-gray-500 shrink-0"
                    onClick={() => copyToClipboard(r.text)}
                  >
                    Copy
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  </div>
}

export default BarCode
